import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { MaterialModule } from '../../../core/shared/material.module';

@Component({
  selector: 'app-formly-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FormlyModule,
    FormlyBootstrapModule,
    MaterialModule,
    NzSelectModule,
  ],
  templateUrl: './formly-modal.component.html',
  styleUrl: './formly-modal.component.scss'
})
export class FormlyModalComponent {
  activeModal = inject(NgbActiveModal, { optional: true });
  @Input() configs: any = {};
  @Input() fields: any[] = [];
  /** Render inside a host panel instead of as a stacked popup. */
  @Input()
  @HostBinding('class.embedded')
  embedded = false;
  /** Hide Cancel/Save footer when the host owns primary actions. */
  @Input() hideFooter = false;
  @Output() saved = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();
  dynamicForm: any = new FormGroup({});

  @ViewChild('fileInputRef') fileInputRef!: ElementRef<HTMLInputElement>;

  model: any = {};
  options: FormlyFormOptions = {};
  isActive = false;
  selectedFile: File | null = null;
  selectedTags: string[] = [];
  fileError = '';

  private readonly defaultFileAcceptByTarget: Record<string, string[]> = {
    testDataGeneration: ['.sql', '.xls', '.xlsx', '.csv'],
    testCaseGenerator: ['.pdf', '.xls', '.xlsx', '.doc', '.docx'],
  };

  /** Predefined tags passed via configs.predefinedTags from the caller. */
  get predefinedTags(): string[] {
    return this.configs?.predefinedTags ?? [];
  }

  /** True when this is a storage connection modal (has type=local/s3 field). */
  get isStorageConnection(): boolean {
    return this.fields.some((f: any) =>
      f.fieldGroup?.some((fg: any) => fg.key === 'type') || f.key === 'type'
    );
  }

  /** Show drop zone only for local file type. */
  get showDropZone(): boolean {
    return this.isStorageConnection && (!this.model.type || this.model.type === 'local');
  }

  private get fileAcceptByTarget(): Record<string, string[]> {
    return this.configs?.fileAcceptByTarget ?? this.defaultFileAcceptByTarget;
  }

  get allowedExtensions(): string[] {
    const map = this.fileAcceptByTarget;
    const target = this.model?.targetAsset;
    const targetKeys = Array.isArray(target) ? target : target ? [target] : [];
    const fromTarget = targetKeys.flatMap((key: string) => map[key] ?? []);
    const fallback = this.configs?.allowedFileExtensions?.length
      ? this.configs.allowedFileExtensions
      : Object.values(map);
    return this.uniqueNormalizedExtensions(fromTarget.length ? fromTarget : fallback);
  }

  get fileAccept(): string {
    return this.allowedExtensions.join(',');
  }

  get fileAcceptHint(): string {
    return this.allowedExtensions
      .map((ext) => ext.replace(/^\./, ''))
      .filter((ext, index, all) => all.indexOf(ext) === index)
      .join(', ');
  }

  ngOnInit() {
    if (this.configs?.model) {
      this.model = { ...this.configs.model };
      const rawTags = this.model.tags;
      if (rawTags) {
        this.selectedTags = Array.isArray(rawTags)
          ? rawTags
          : String(rawTags).split(',').map((t: string) => t.trim()).filter(Boolean);
      }
    }
    this.attachTargetAssetChangeHook();
  }

  get canSubmit(): boolean {
    if (this.model?.type === 'local') {
      return !!this.selectedFile && !!this.dynamicForm?.valid;
    }
    return !!this.dynamicForm?.valid;
  }

  dismiss(): void {
    if (this.embedded) {
      this.cancelled.emit();
      return;
    }
    this.activeModal?.dismiss('Cross click');
  }

  isTagSelected(tag: string): boolean {
    return this.selectedTags.includes(tag);
  }

  togglePresetTag(tag: string): void {
    if (this.isTagSelected(tag)) {
      this.selectedTags = this.selectedTags.filter(t => t !== tag);
    } else {
      this.selectedTags = [...this.selectedTags, tag];
    }
  }

  /** Keep selectedTags in sync when the nz-select model changes. */
  onTagsChange(tags: string[]): void {
    this.selectedTags = tags;
  }

  triggerFileInput(): void {
    this.fileInputRef?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.selectedFile = null;
      this.fileError = '';
      return;
    }

    if (!this.isFileAllowed(file)) {
      this.selectedFile = null;
      this.fileError = `Invalid file type. Allowed: ${this.fileAcceptHint || 'select a target asset first'}`;
      input.value = '';
      return;
    }

    this.selectedFile = file;
    this.fileError = '';
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      return;
    }
    if (this.showDropZone && this.selectedFile && !this.isFileAllowed(this.selectedFile)) {
      this.fileError = `Invalid file type. Allowed: ${this.fileAcceptHint}`;
      return;
    }

    const result = {
      value: {
        ...this.dynamicForm.value,
        // Prefer live model for type (form value can lag select change)
        type: this.model?.type || this.dynamicForm.value?.type,
        tags: this.selectedTags,
        fileUpload: this.selectedFile,
      },
      fields: this.fields,
    };

    if (this.embedded) {
      this.saved.emit(result);
      return;
    }
    this.activeModal?.close(result);
  }

  private attachTargetAssetChangeHook(): void {
    const targetField = this.findFieldByKey(this.fields, 'targetAsset');
    if (!targetField) return;

    const existingOnChange = targetField.templateOptions?.change;
    targetField.templateOptions = {
      ...targetField.templateOptions,
      change: (field: any, event: any) => {
        if (typeof existingOnChange === 'function') {
          existingOnChange(field, event);
        }
        this.clearSelectedFile();
      },
    };
  }

  private findFieldByKey(fields: any[], key: string): any | null {
    for (const field of fields ?? []) {
      if (field?.key === key) return field;
      const nested = this.findFieldByKey(field?.fieldGroup ?? [], key);
      if (nested) return nested;
    }
    return null;
  }

  private clearSelectedFile(): void {
    this.selectedFile = null;
    this.fileError = '';
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  private isFileAllowed(file: File): boolean {
    const allowed = this.allowedExtensions;
    if (!allowed.length || !this.model?.targetAsset) {
      return false;
    }
    const name = (file.name || '').toLowerCase();
    return allowed.some((ext) => name.endsWith(ext.toLowerCase()));
  }

  /** Normalize ".PDF" / "pdf" to ".pdf" and drop duplicates across target lists. */
  private uniqueNormalizedExtensions(extensions: unknown): string[] {
    const seen = new Set<string>();
    const unique: string[] = [];
    const visit = (value: unknown) => {
      if (value == null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }
      String(value)
        .split(',')
        .forEach((part) => {
          const trimmed = part.trim().toLowerCase();
          if (!trimmed) return;
          const normalized = trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
          if (seen.has(normalized)) return;
          seen.add(normalized);
          unique.push(normalized);
        });
    };
    visit(extensions);
    return unique;
  }
}
